/**
 * The native "Save as" dialog, where the browser has one.
 *
 * Chrome and Edge expose `showSaveFilePicker`, which opens the OS file explorer so the drafter
 * chooses the folder. Firefox and Safari do not, and there the caller falls back to a download
 * link. The picker needs a user gesture that expires within seconds, so it is opened on the
 * click, before the package is built, and the bytes are written to the handle afterwards.
 */

interface SaveHandle {
  createWritable(): Promise<{ write(data: Blob): Promise<void>; close(): Promise<void> }>;
}

type SaveFilePicker = (options: {
  suggestedName: string;
  id: string;
  types: { description: string; accept: Record<string, string[]> }[];
}) => Promise<SaveHandle>;

export type SaveTarget = { readonly write: (blob: Blob) => Promise<void> };

export function canChooseSaveLocation(): boolean {
  return typeof (window as unknown as { showSaveFilePicker?: unknown }).showSaveFilePicker === "function";
}

/** Resolves to null when the person dismisses the dialog. */
export async function chooseSaveTarget(suggestedName: string): Promise<SaveTarget | null> {
  const picker = (window as unknown as { showSaveFilePicker: SaveFilePicker }).showSaveFilePicker.bind(window);
  try {
    const handle = await picker({
      suggestedName,
      id: "plan-forge-package",
      types: [{ description: "X1 conversion package", accept: { "application/json": [".json"] } }],
    });
    return {
      write: async (blob) => {
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
      },
    };
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") return null;
    throw error;
  }
}
